import { AxiosResponse } from 'axios';
import { all, AllEffect, call, CallEffect, ForkEffect, put, PutEffect, select, SelectEffect, takeEvery } from 'redux-saga/effects';
import { RemoteRecord } from 'b2b-apps/core/common/redux/RemoteRecord';
import { getCookie, removeCookie, setCookie } from 'b2b-apps/core/common/utils/storage/cookies';
import { getProductBySku } from 'b2b-apps/core/product/redux/products/full/selectors';
import { ProductFullType } from 'b2b-apps/core/product/redux/products/full/types';
import { getProductTileBySku } from 'b2b-apps/core/product/redux/products/tile/selectors';
import { ProductTileType } from 'b2b-apps/core/product/redux/products/tile/types';
import { updateProductsCompareSettings } from 'b2b-apps/core/settings/redux/actionCreators';
import { SettingsProductsCompareType } from 'b2b-apps/core/settings/redux/types';
import { requestApiSaga } from 'b2b-apps/shop/common/redux/api/sagas';
import { reportError } from 'b2b-apps/shop/common/redux/errors/actionCreators';
import { parseError } from 'b2b-apps/shop/common/utils/api/error';
import { ProductsCompare } from 'b2b-apps/shop/product/api/ProductsCompare';
import { MAX_NUMBER_OF_PRODUCTS_TO_COMPARE, ProductsCompare as ProductsCompareType } from 'b2b-common/core/product/api/ProductsCompare.types';
import { fetchProductsCompare, fetchProductsCompareFulfilled, fetchProductsCompareRejected } from './actionCreators';
import { ADD_PRODUCT_TO_COMPARE, CLEAR_PRODUCTS_COMPARE, FETCH_PRODUCTS_COMPARE, REMOVE_PRODUCT_FROM_COMPARE } from './actionTypes';
import {
  AddProductToCompareAction,
  COMPARE_PRODUCTS_COOKIE_NAME,
  FetchProductsCompareAction,
  RemoveProductFromCompareAction,
} from './types';

function* fetchProductsCompareSaga(
  action: FetchProductsCompareAction,
): Generator<
  CallEffect | PutEffect,
  void,
  AxiosResponse<ProductsCompareType>
  > {
  const { skus } = action.payload;

  try {
    const response: AxiosResponse<ProductsCompareType> = yield call(
      requestApiSaga, ProductsCompare.fetch(skus),
    );
    yield put(fetchProductsCompareFulfilled(ProductsCompare.mapResponseToState(response)));
  } catch (error) {
    yield put(fetchProductsCompareRejected(parseError(error)));
    yield put(reportError(action.type, error, action.payload));
  }
}

function* getProduct(sku: string): Generator<
  SelectEffect,
  ProductFullType | ProductTileType | null,
  RemoteRecord<ProductTileType> & RemoteRecord<ProductFullType>
  > {
  const productTileRecord = yield select(getProductTileBySku, sku);

  if (productTileRecord.data) {
    return productTileRecord.data;
  }

  const productFullRecord = yield select(getProductBySku, sku);

  if (productFullRecord.data) {
    return productFullRecord.data;
  }

  return null;
}

function* addProductToCompareSaga(action: AddProductToCompareAction): Generator<
  SelectEffect | CallEffect | PutEffect,
  void,
  & ProductFullType
  & ProductTileType
  & SettingsProductsCompareType
  & AxiosResponse<SettingsProductsCompareType>
  > {
  const { sku } = action.payload;
  const product = yield call(getProduct, sku);

  if (!product) {
    return;
  }

  const cookie = getCookie(COMPARE_PRODUCTS_COOKIE_NAME) as SettingsProductsCompareType;
  const productPreview = {
    sku: product.sku,
    name: product.description.title,
    cover: product.cover?.sizes.find(({ size }) => size === 'L')?.link || null,
  };
  const products = cookie?.products
    ? [...cookie.products?.filter(p => p.sku !== sku), productPreview]
    : [productPreview];

  if (products.length > MAX_NUMBER_OF_PRODUCTS_TO_COMPARE) {
    return;
  }

  const settings = { isClosed: false, products };

  yield put(updateProductsCompareSettings(settings));
  setCookie(COMPARE_PRODUCTS_COOKIE_NAME, { ...settings, skus: ['1600385', '486889'] });
}

function* removeProductFromCompareSaga(action: RemoveProductFromCompareAction): Generator<
  SelectEffect | CallEffect | PutEffect,
  void,
  SettingsProductsCompareType & AxiosResponse<SettingsProductsCompareType>
  > {
  const cookie = getCookie(COMPARE_PRODUCTS_COOKIE_NAME) as SettingsProductsCompareType;

  if (!cookie || !cookie.products?.length) {
    return;
  }

  const { sku } = action.payload;
  const products = cookie.products.filter(product => product.sku !== sku);
  const settings = { ...cookie, products };

  yield put(updateProductsCompareSettings(settings));
  setCookie(COMPARE_PRODUCTS_COOKIE_NAME, settings);
}

function* clearProductsCompareSaga(action: RemoveProductFromCompareAction): Generator<
  SelectEffect | CallEffect | PutEffect,
  void,
  SettingsProductsCompareType & AxiosResponse<SettingsProductsCompareType>
  > {
  yield put(fetchProductsCompare([]));
  removeCookie(COMPARE_PRODUCTS_COOKIE_NAME);
}

export function* productsCompareSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeEvery(FETCH_PRODUCTS_COMPARE, fetchProductsCompareSaga),
    takeEvery(ADD_PRODUCT_TO_COMPARE, addProductToCompareSaga),
    takeEvery(REMOVE_PRODUCT_FROM_COMPARE, removeProductFromCompareSaga),
    takeEvery(CLEAR_PRODUCTS_COMPARE, clearProductsCompareSaga),
  ]);
}
